'use client';

import { bunja } from 'bunja';
import { endOfYesterday, startOfYesterday } from 'date-fns';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getUserPointApi, getUserPointHistoryApi } from '@flipster/apis/v1/user';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import { convertDate, dateToEpochString } from '@flipster/utils/datetime';
import Num from '@flipster/utils/num';

export const sparkRewardPageBunja = bunja(() => {
  const { walletsAtom } = bunja.use(walletsBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  // https://prex.atlassian.net/browse/PREX-18941?focusedCommentId=56485
  const usdtBalanceAtom = atom((get) => {
    const usdtBalanceString = get(walletsAtom)?.[UsdtCurrency]?.walletBalanceExclWithdrawalPending;
    if (!usdtBalanceString) return;
    return Num(usdtBalanceString);
  });

  const getUserPointAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getUserPointApi', isCookieLoggedIn],
      queryFn: getUserPointApi,
      enabled: !!isCookieLoggedIn,
    };
  });

  const getYesterdayUserPointHistoryAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);

    return {
      queryKey: ['getYesterdayUserPointHistory', isCookieLoggedIn],
      queryFn: () =>
        getUserPointHistoryApi({
          startTime: dateToEpochString(startOfYesterday()),
          endTime: dateToEpochString(endOfYesterday()),
        }),
      enabled: !!isCookieLoggedIn,
    };
  });

  const userPointAtom = atom((get) => {
    const pointsString = get(getUserPointAtom).data?.data.points;
    return pointsString ? Num(pointsString) : undefined;
  });

  const updatedAtAtom = atom((get) => {
    const updatedAtString = get(getUserPointAtom).data?.data.updatedAt;
    return convertDate(updatedAtString) ?? undefined;
  });

  const earnedYesterdayAtom = atom((get) => {
    return get(getYesterdayUserPointHistoryAtom).data?.data.pointHistories.reduce((memo, pointHistory) => {
      const earnedPoints = Num(pointHistory.earnedPoints);
      if (earnedPoints.isNegative()) return memo; // 포인트를 사용한 내역은 제외
      return memo.plus(earnedPoints);
    }, Num(0));
  });

  const multiplierRulesAtom = atom((get) => get(getUserPointAtom).data?.data.multiplierRules);

  const percentilePointsAtom = atom((get) =>
    get(getUserPointAtom).data?.data.percentilePoints.map((x) => ({
      percentile: x.percentile,
      pointAmount: Num(x.pointAmount),
    })),
  );

  return {
    userPointAtom,
    updatedAtAtom,
    earnedYesterdayAtom,
    multiplierRulesAtom,
    usdtBalanceAtom,
    percentilePointsAtom,
  };
});
sparkRewardPageBunja.debugLabel = 'sparkRewardPageBunja';
